import { CircleIcon } from "lucide-react";
import { TooltipProps } from "recharts";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import { IgTypography } from "@intelligems/ig-theme";
import { GraphConfig, GraphLegend, LineData } from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import { Stack, SxProps } from "@mui/system";
import { StrippedCircle } from "@src/images/Icons/StrippedCircleIcon";

import { useGraphValueFormatter } from "../hooks/use-graph-value-formatter";
import { formatChartLabel } from "../utils";

type Props = {
  colors?: string[];
  dataTypes?: GraphConfig["dataTypes"];
  labelFormatting?: (label: number) => string | number | undefined;
  legends?: LineData[];
  dateFormat?: string;
  useZeroInsteadOfNA?: boolean;
};

export const useGetTooltipProps = ({
  colors = TimeSeriesColors,
  dataTypes,
  labelFormatting,
  legends,
  dateFormat,
  useZeroInsteadOfNA,
}: Props): Partial<TooltipProps<ValueType, NameType>> => {
  const graphValueFormatter = useGraphValueFormatter();
  const theme = useTheme();
  if (!legends) {
    return {};
  }

  const typographyStyles: SxProps = {
    color: theme.igPrimitives.gray[500],
    fontSize: "14px",
    lineHeight: "16px",
    letterSpacing: ".4px",
  };

  const renderContent = ({
    label,
    payload,
  }: TooltipProps<ValueType, NameType>) => {
    return (
      <Stack
        spacing={1.2}
        sx={{
          borderRadius: "5px",
          backgroundColor: "rgba(73, 76, 80, 0.92)",
          padding: "10px",
        }}
      >
        <IgTypography
          sx={{
            ...typographyStyles,
            fontWeight: 700,
          }}
        >
          {labelFormatting
            ? labelFormatting(label)
            : formatChartLabel(label, dateFormat)}
        </IgTypography>

        {payload?.map((item, index) => {
          const legend = legends?.find((legend) => legend.id === item.dataKey);
          const value = item.payload[item.dataKey!];
          const Icon = legend?.isDotted ? StrippedCircle : CircleIcon;

          if (!legend) {
            return;
          }

          return (
            <Stack
              direction="row"
              justifyContent="space-between"
              key={index}
              spacing={4}
            >
              <Stack alignItems="center" direction="row" gap={0.5}>
                <Icon
                  fill={legend?.color ?? colors[index]}
                  size={14}
                  stroke={legend?.color ?? colors[index]}
                />

                <IgTypography sx={typographyStyles}>
                  {legend?.name}
                </IgTypography>
              </Stack>

              <IgTypography
                sx={{
                  ...typographyStyles,
                  fontWeight: 700,
                }}
              >
                {value !== undefined && value !== null
                  ? graphValueFormatter({
                      isAxisTick: false,
                      value,
                      configDataTypes: dataTypes
                        ? dataTypes
                        : [(legend as GraphLegend)?.dataType || "number"],
                    })
                  : useZeroInsteadOfNA
                    ? 0
                    : "N/A"}
              </IgTypography>
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return {
    content: (props) => renderContent(props),
    position: { y: 145 },
    cursor: { fill: "transparent" },
    wrapperStyle: { zIndex: 50 },
    filterNull: false,
  };
};
